import React from "react";
import { X } from "lucide-react";
import { fmtDate, getReadableOrderId, statusClass } from "../utils/inventory.helpers";

function pick(...values) {
  return values.find(
    (value) => value !== undefined && value !== null && String(value).trim() !== ""
  );
}

function getItemStatus(item = {}) {
  const explicit = pick(item.status, item.itemStatus, item.item_status);
  if (explicit) return String(explicit).toLowerCase();

  const approvedQty = pick(item.approvedQty, item.qty_approved, item.approved_qty);
  if (approvedQty === undefined || approvedQty === null) return "pending";
  return Number(approvedQty) === 0 ? "rejected" : "approved";
}

function getRejectReason(item = {}) {
  return pick(item.reason, item.rejectReason, item.reject_reason) || "";
}

export default function OrderDetailModal({
  open,
  orderDetail,
  onClose,
}) {
  if (!open || !orderDetail) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#f8dde1] px-6 py-4">
          <div>
            <h3 className="text-[18px] font-bold text-slate-800">Order Detail</h3>
            <p className="mt-1 text-[13px] font-semibold text-slate-600">
              {getReadableOrderId(orderDetail)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] p-2 text-slate-500 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[72vh] overflow-y-auto px-6 py-5">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-[14px] border border-[#f2c6cc] p-4">
              <div className="text-[12px] font-semibold text-slate-500">Status</div>
              <div className="mt-2 text-[14px] font-semibold text-slate-800">
                {orderDetail.status || "-"}
              </div>
            </div>
            <div className="rounded-[14px] border border-[#f2c6cc] p-4">
              <div className="text-[12px] font-semibold text-slate-500">Warehouse</div>
              <div className="mt-2 text-[14px] font-semibold text-slate-800">
                {pick(
                  orderDetail.warehouse_name,
                  orderDetail.warehouseName,
                  orderDetail.warehouse_id,
                  orderDetail.warehouseId
                ) || "-"}
              </div>
            </div>
            <div className="rounded-[14px] border border-[#f2c6cc] p-4">
              <div className="text-[12px] font-semibold text-slate-500">Created At</div>
              <div className="mt-2 text-[14px] font-semibold text-slate-800">
                {fmtDate(pick(orderDetail.created_at, orderDetail.createdAt))}
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-[16px] border border-[#f2c6cc] bg-[#fffdfd] p-4">
            <div className="mb-3 text-[14px] font-semibold text-slate-800">Items</div>
            <div className="space-y-3">
              {(orderDetail.items || []).map((item, index) => {
                const status = getItemStatus(item);
                const reason = getRejectReason(item);
                const approvedQty = pick(item.approvedQty, item.qty_approved, item.approved_qty);

                return (
                  <div
                    key={item.id || `${item.productId || item.product_id || "item"}-${index}`}
                    className="rounded-[14px] border border-[#f7d8de] bg-white p-4"
                  >
                    <div className="grid gap-3 md:grid-cols-5">
                      <div>
                        <div className="text-[12px] font-semibold text-slate-500">Product</div>
                        <div className="mt-1 text-[14px] font-semibold text-slate-800">
                          {pick(item.name, item.productName, item.product_name) || "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[12px] font-semibold text-slate-500">SKU</div>
                        <div className="mt-1 text-[14px] text-slate-700">
                          {pick(item.sku, item.variantSku, item.variant_sku) || "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[12px] font-semibold text-slate-500">Requested</div>
                        <div className="mt-1 text-[14px] text-slate-700">
                          {Number(pick(item.requestedQty, item.qty_requested, item.requested_qty) || 0)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[12px] font-semibold text-slate-500">Approved</div>
                        <div className="mt-1 text-[14px] text-slate-700">
                          {approvedQty === undefined || approvedQty === null
                            ? "-"
                            : Number(approvedQty)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[12px] font-semibold text-slate-500">Item Status</div>
                        <span
                          className={[
                            "mt-1 inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize",
                            statusClass(status),
                          ].join(" ")}
                        >
                          {status || "-"}
                        </span>
                      </div>
                    </div>

                    {status === "rejected" ? (
                      <div className="mt-4 rounded-[12px] border border-rose-100 bg-rose-50 px-4 py-3">
                        <div className="text-[12px] font-semibold text-rose-700">Reject Reason</div>
                        <div className="mt-1 whitespace-pre-wrap break-words text-[14px] text-rose-900">
                          {reason || "-"}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
